<div class="flex flex-col gap-6 max-w-lg">
  <!-- En-tête de la modale -->
  <div class="flex items-start justify-between gap-4">
    <div class="flex flex-col gap-1.5">
      @if (event.sourceId === 'french-holidays') {
        <div class="flex items-center gap-2">
          <span
            class="text-xs font-medium px-2.5 py-0.5 rounded-full border bg-violet-3 text-violet-11 border-violet-6"
          >
            Jour férié
          </span>
        </div>
      } @else if (event.status) {
        @let statusBadge = getStatusBadge(event.status);
        <div class="flex items-center gap-2">
          <span [class]="statusBadge.class" class="text-xs font-medium px-2.5 py-0.5 rounded-full border">
            {{ statusBadge.label }}
          </span>
        </div>
      }

      <h3 class="text-xl font-bold tracking-tight text-neutral-12 m-0">
        {{ event.title }}
      </h3>
    </div>

    <button
      type="button"
      mat-icon-button
      (click)="dialogRef.close()"
      aria-label="Fermer"
      class="text-neutral-10 hover:text-neutral-12 hover:bg-neutral-3 rounded-md"
    >
      <mat-icon class="!text-xl !w-5 !h-5 flex items-center justify-center">close</mat-icon>
    </button>
  </div>

  <!-- Informations principales de date & heure -->
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-neutral-2/60 border border-neutral-4 rounded-xl">
    <div class="flex items-center gap-2.5">
      <div class="w-8 h-8 rounded-lg bg-primary-3 text-primary-11 flex items-center justify-center shrink-0">
        <mat-icon class="!text-lg !w-4 !h-4 flex items-center justify-center">event</mat-icon>
      </div>
      <div class="flex flex-col">
        <span class="text-[11px] font-medium text-neutral-10 uppercase tracking-wider">Date</span>
        <span class="text-xs font-semibold text-neutral-12">{{ formattedDate }}</span>
      </div>
    </div>

    <div class="flex items-center gap-2.5">
      <div class="w-8 h-8 rounded-lg bg-primary-3 text-primary-11 flex items-center justify-center shrink-0">
        <mat-icon class="!text-lg !w-4 !h-4 flex items-center justify-center">schedule</mat-icon>
      </div>
      <div class="flex flex-col">
        <span class="text-[11px] font-medium text-neutral-10 uppercase tracking-wider">Horaire</span>
        <span class="text-xs font-semibold text-neutral-12">{{ formattedTime }}</span>
      </div>
    </div>
  </div>

  <!-- Lieu / Adresse -->
  @if (event.location?.address) {
    <div class="flex items-start gap-3">
      <div
        class="w-8 h-8 rounded-lg bg-neutral-3 text-neutral-11 flex items-center justify-center shrink-0 mt-0.5"
      >
        <mat-icon class="!text-lg !w-4 !h-4 flex items-center justify-center">
          {{ event.location?.lockMessage ? 'lock' : 'place' }}
        </mat-icon>
      </div>
      <div class="flex flex-col gap-1.5 flex-1 min-w-0">
        <span class="text-xs font-semibold text-neutral-11">Lieu du rendez-vous</span>
        <span class="text-xs text-neutral-12 leading-relaxed font-medium">{{ event.location?.address }}</span>

        @if (event.location?.lockMessage) {
          <div
            class="flex items-start gap-2 p-2.5 rounded-lg bg-amber-2/60 border border-amber-4 text-amber-11 text-xs"
          >
            <mat-icon class="!text-base !w-4 !h-4 text-amber-10 shrink-0 mt-0.5">lock</mat-icon>
            <span class="leading-relaxed">{{ event.location?.lockMessage }}</span>
          </div>
        }

        <a
          [href]="getMapsUrl(event.location!.address!)"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs font-medium text-primary-11 hover:underline mt-0.5 inline-flex items-center gap-1"
        >
          <span>Ouvrir dans Google Maps</span>
          <mat-icon class="!text-xs !w-3 !h-3 flex items-center justify-center">open_in_new</mat-icon>
        </a>
      </div>
    </div>
  }

  <!-- Description / Notes -->
  @if (event.description) {
    <div class="flex items-start gap-3">
      <div
        class="w-8 h-8 rounded-lg bg-neutral-3 text-neutral-11 flex items-center justify-center shrink-0 mt-0.5"
      >
        <mat-icon class="!text-lg !w-4 !h-4 flex items-center justify-center">notes</mat-icon>
      </div>
      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <span class="text-xs font-semibold text-neutral-11">Notes</span>
        <p class="text-xs text-neutral-12 whitespace-pre-wrap m-0 leading-relaxed">{{ event.description }}</p>
      </div>
    </div>
  }

  <!-- Contextes concernés (Bien immobilier, Messagerie, etc.) -->
  @if (event.contexts && event.contexts.length > 0) {
    <div class="flex flex-col gap-2.5">
      <span class="text-xs font-semibold text-neutral-11"> Contextes associés </span>

      <div class="flex flex-col gap-2">
        @for (item of event.contexts; track item.id + item.type) {
          <div
            role="button"
            tabindex="0"
            (click)="onNavigateContext(item)"
            (keydown.enter)="onNavigateContext(item)"
            [matTooltip]="item.type === 'property' ? 'Consulter la fiche du bien' : 'Accéder à la messagerie'"
            class="group relative flex items-center gap-3.5 p-3 rounded-xl border border-neutral-4 bg-neutral-2/40 hover:bg-neutral-2 hover:border-primary-8 cursor-pointer transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary-9"
          >
            <!-- Image / Vignette ou Icône -->
            @if (item.imageUrl) {
              <div
                class="relative w-14 h-14 rounded-lg overflow-hidden shrink-0 border border-neutral-4 bg-neutral-3"
              >
                <img
                  [src]="item.imageUrl"
                  [alt]="item.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              </div>
            } @else {
              <div
                class="w-12 h-12 rounded-lg bg-primary-3 text-primary-11 flex items-center justify-center shrink-0 border border-primary-5 group-hover:bg-primary-4 transition-colors"
              >
                <mat-icon class="!text-xl !w-5 !h-5 flex items-center justify-center">
                  {{ item.icon || (item.type === 'property' ? 'domain' : 'forum') }}
                </mat-icon>
              </div>
            }

            <!-- Détails du contexte -->
            <div class="flex flex-col min-w-0 flex-1">
              <div class="flex items-center gap-2 mb-0.5">
                @if (item.badge) {
                  <span
                    class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-neutral-3 text-neutral-11 border border-neutral-5"
                  >
                    {{ item.badge }}
                  </span>
                }
              </div>

              <span
                class="text-sm font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
              >
                {{ item.title }}
              </span>

              @if (item.subtitle) {
                <span class="text-xs text-neutral-10 truncate">{{ item.subtitle }}</span>
              }

              <!-- Attributs clés (Surface, Pièces, Prix, Ville) -->
              @if (item.attributes && item.attributes.length > 0) {
                <div class="flex items-center flex-wrap gap-1.5 mt-1.5">
                  @for (attr of item.attributes; track attr.label) {
                    <span
                      class="inline-flex items-center gap-1 text-[11px] font-medium text-neutral-11 bg-neutral-3/70 px-2 py-0.5 rounded-md"
                    >
                      @if (attr.icon) {
                        <mat-icon class="!text-xs !w-3.5 !h-3.5 text-neutral-10">{{ attr.icon }}</mat-icon>
                      }
                      <span>{{ attr.value }}</span>
                    </span>
                  }
                </div>
              }
            </div>

            <!-- Flèche d'action -->
            <div class="shrink-0 text-neutral-8 group-hover:text-primary-11 transition-colors pr-1">
              <mat-icon
                class="!text-lg !w-5 !h-5 flex items-center justify-center group-hover:translate-x-0.5 transition-transform"
              >
                arrow_forward
              </mat-icon>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Participants -->
  @if (event.attendees && event.attendees.length > 0) {
    <div class="flex flex-col gap-2">
      <span class="text-xs font-semibold text-neutral-11"> Participants ({{ event.attendees.length }}) </span>
      <div class="flex flex-col gap-2 border border-neutral-4 rounded-xl p-3 bg-neutral-2/30">
        @for (att of event.attendees; track att.name || att.email) {
          <div class="flex items-center justify-between gap-3 text-xs">
            <div class="flex items-center gap-2 min-w-0">
              <div
                class="w-7 h-7 rounded-full bg-neutral-4 text-neutral-12 font-bold text-[11px] flex items-center justify-center overflow-hidden shrink-0"
              >
                @if (att.avatarUrl) {
                  <img
                    [src]="att.avatarUrl"
                    [alt]="att.name || 'Participant'"
                    class="w-full h-full object-cover"
                  />
                } @else {
                  <span>{{ (att.name || 'U').charAt(0).toUpperCase() }}</span>
                }
              </div>
              <div class="flex flex-col min-w-0">
                <span class="font-medium text-neutral-12 truncate">{{
                  att.name || att.email || 'Participant'
                }}</span>
                <span class="text-[10px] text-neutral-10">
                  {{ att.role === 'organizer' ? 'Organisateur' : 'Participant' }}
                </span>
              </div>
            </div>

            <span
              [class]="
                att.status === 'accepted'
                  ? 'text-jade-11 bg-jade-3'
                  : att.status === 'declined'
                    ? 'text-ruby-11 bg-ruby-3'
                    : 'text-amber-11 bg-amber-3'
              "
              class="text-[10px] font-medium px-2 py-0.5 rounded-full"
            >
              {{
                att.status === 'accepted' ? 'Accepté' : att.status === 'declined' ? 'Refusé' : 'En attente'
              }}
            </span>
          </div>
        }
      </div>
    </div>
  }

  <!-- Pied de page avec bouton fermer -->
  <div class="flex justify-end pt-2">
    <button type="button" mat-flat-button (click)="dialogRef.close()">Fermer</button>
  </div>
</div>
